import React, { useState, useEffect, useCallback, Fragment } from 'react';
import { Breadcrumbs, H5 } from '../../../AbstractElements';
import { Card, CardBody, CardHeader, Col, Container, Row, Input, Media } from 'reactstrap';
import DataTable from '../../Common/DataTable';
import { PlusCircle, Edit, Trash2 } from 'react-feather';
import BankDataModal from './BankDataModal';
import DeleteConfirmationModal from '../../Common/Modals/DeleteConfirmationModal';
import { confirmDelete, fetchTableData } from '../../../Api/ApiMethods';

const Banks = () => {
    const [selectedRows, setSelectedRows] = useState([]);
    const [clearSelectedRows, setClearSelectedRows] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [data, setData] = useState([]); // filtered list
    const [fullData, setFullData] = useState([]); // complete list fetched once
    const [search, setSearch] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [selectedBank, setSelectedBank] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const PAGE_SIZE = 1000; // load once, filter on the client
    const bankApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/bank';

    const handleRowSelected = useCallback(state => {
        setSelectedRows(state.selectedRows);
    }, []);

    const applyFilter = useCallback((items, term) => {
        const normalized = term.trim().toLocaleLowerCase('tr-TR');
        if (!normalized) return items;
        return items.filter(item => (item.bankName || '').toLocaleLowerCase('tr-TR').includes(normalized));
    }, []);

    const handleSearch = (event) => {
        event.preventDefault();
        setSearch(event.target.value);
    };

    const loadBanks = useCallback(async () => {
        setIsLoading(true);
        try {
            await fetchTableData({
                apiEndpoint: bankApiUrl,
                page: 1,
                pageSize: PAGE_SIZE,
                setDataCallback: (items) => {
                    setFullData(items);
                    setData(items);
                },
            });
        } finally {
            setIsLoading(false);
        }
    }, [bankApiUrl, PAGE_SIZE]);

    useEffect(() => {
        loadBanks();
    }, [loadBanks]);

    useEffect(() => {
        setData(applyFilter(fullData, search));
    }, [search, fullData, applyFilter]);

    const toggleDeleteModal = () => setDeleteModal(!deleteModal);

    const openAddModal = () => {
        setModalMode('add');
        setSelectedBank(null);
        setModalOpen(true);
    };

    const openModifyModal = (bank) => {
        setModalMode('modify');
        setSelectedBank(bank);
        setModalOpen(true);
    };

    const columns = [
        { name: 'Banka Adı', selector: row => row.bankName, sortable: true },
        { name: 'Banka Kodu', selector: row => row.bankCode, sortable: true },
        { name: 'Şube Adı', selector: row => row.branchName, sortable: true },
        { name: 'Şube Kodu', selector: row => row.branchCode, sortable: true },
        { name: 'IBAN', selector: row => row.iban, sortable: true },
        { name: 'SWIFT/BIC Kodu', selector: row => row.swiftBicCode, sortable: true },
        { name: 'Para Birimi', selector: row => row.currency, sortable: true },
        {
            name: 'Düzenle',
            cell: (row) => row.code ? null : (
                <div
                    className="btn btn-outline-warning"
                    onClick={() => openModifyModal(row)}
                >
                    <Edit size={18} />
                </div>
            ),
            ignoreRowClick: true,
            right: true,
            button: true
        },
    ];

    return (
        <>
            <Breadcrumbs parent='Hazır Girdi Alanları' title='Bankalar' />
            <Container fluid={true}>
                <Row>
                    <Col sm='12'>
                        <Card>
                            <CardHeader>
                                <H5>Bankalar</H5>
                                <span>Tanımlanmış olan bankalar aşağıda listelenmektedir.</span>
                            </CardHeader>
                            <CardBody>
                                <div className="d-flex justify-content-end align-items-center w-100 file-content flex-wrap">
                                    <div className='form-inline me-2 mt-3 p-0 custom-responsive-width'>
                                        <div className='mb-0 form-group d-flex align-items-center border rounded px-2' style={{ height: '35px' }}>
                                            <i className='fa fa-search text-muted'></i>
                                            <Input
                                                type="text"
                                                placeholder="Ara.."
                                                value={search}
                                                onChange={(e) => handleSearch(e)}
                                                className="border-0 ms-2 shadow-none"
                                                style={{ height: '33px', flexGrow: 1 }}
                                            />
                                        </div>
                                    </div>
                                    <Media body className='text-end mt-3'>
                                        <div className="btn btn-outline-primary ms-2" onClick={openAddModal} style={{ height: '35px', display: 'inline-flex', alignItems: 'center' }}>
                                            <PlusCircle size={18} className="me-1"/>
                                            {' Banka Ekle'}
                                        </div>
                                    </Media>
                                    {(selectedRows.length !== 0) &&
                                        <div className='btn btn-outline-danger ms-2 mt-3' onClick={toggleDeleteModal} style={{ height: '35px', display: 'inline-flex', alignItems: 'center' }}>
                                            <Trash2 size={18} className="me-1"/>
                                            {' Seçilenleri Sil'}
                                        </div>
                                    }
                                </div>

                                <div style={{ height: "40px" }}></div>

                                <Fragment>
                                    <DataTable
                                        columns={columns}
                                        data={data}
                                        onSelectedRowsChange={handleRowSelected}
                                        pagination
                                        selectableRows
                                        selectableRowDisabled={row => !!row.code}
                                        clearSelectedRows={clearSelectedRows}
                                        progressPending={isLoading}
                                        progressComponent={<div className="py-4 text-center">Yükleniyor...</div>}
                                        noDataComponent={isLoading ? null : <div className="p-4 text-center">Banka bulunamadı.</div>}
                                    />
                                </Fragment>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
            <BankDataModal
                apiEndpoint={bankApiUrl}
                isOpen={modalOpen}
                toggle={() => setModalOpen(!modalOpen)}
                mode={modalMode}
                selectedBank={selectedBank}
                refreshData={loadBanks}
            />
            <DeleteConfirmationModal
                isOpen={deleteModal}
                toggle={toggleDeleteModal}
                itemsToDelete={selectedRows}
                displayKey="bankName"
                onConfirm={() =>
                    confirmDelete({
                        apiEndpoint: bankApiUrl,
                        selectedItems: selectedRows,
                        toggleModalCallback: toggleDeleteModal,
                        clearSelectionCallback: () => {
                            setSelectedRows([]);
                            setClearSelectedRows(!clearSelectedRows);
                        },
                        refreshDataCallback: loadBanks,
                    })
                }
            />
        </>
    );
};

export default Banks;
